import {Tolgee, DevTools, TranslationKey} from "@tolgee/react";
import {FormatIcu} from "@tolgee/format-icu";
import {useMemo} from "react";
import {RetryingBackendFetch} from "./retryingBackendFetch";
import {tolgeeBackendPrefix} from "./backendPrefix";

export type LanguageContext = "internal" | "user" | "account" | "accountInternal" | "accountExternal";

export type Language = {
  code: string;
  nameKey: TranslationKey;
  // Where this language can be used
  limitedTo?: LanguageContext[];
};

const availableLanguages: Language[] = [
  {code: "xx", nameKey: "c.Gibberish", limitedTo: ["internal"]},
  {code: "ar", nameKey: "c.Arabic", limitedTo: ["accountExternal"]},
  {code: "zh-CN", nameKey: "c.Chinese_(Simplified)", limitedTo: ["accountExternal"]},
  {code: "zh-TW", nameKey: "c.Chinese_(Traditional)", limitedTo: ["accountExternal"]},
  {code: "cs", nameKey: "c.Czech", limitedTo: ["accountExternal"]},
  {code: "nl-NL", nameKey: "c.Dutch"},
  {code: "en-GB", nameKey: "c.English_(UK)"},
  {code: "en-US", nameKey: "c.English_(US)"},
  {code: "fr", nameKey: "c.French", limitedTo: ["accountExternal"]},
  {code: "fr-FR", nameKey: "c.French_(France)", limitedTo: ["internal", "accountInternal"]},
  {code: "de-DE", nameKey: "c.German"},
  {code: "he", nameKey: "c.Hebrew", limitedTo: ["accountExternal"]},
  {code: "hi", nameKey: "c.Hindi", limitedTo: ["accountExternal"]},
  {code: "id", nameKey: "c.Indonesian", limitedTo: ["accountExternal"]},
  {code: "it", nameKey: "c.Italian", limitedTo: ["accountExternal"]},
  {code: "ja", nameKey: "c.Japanese", limitedTo: ["accountExternal"]},
  {code: "ko", nameKey: "c.Korean", limitedTo: ["accountExternal"]},
  {code: "pl", nameKey: "c.Polish", limitedTo: ["accountExternal"]},
  {code: "pt", nameKey: "c.Portuguese", limitedTo: ["accountExternal"]},
  {code: "ru", nameKey: "c.Russian", limitedTo: ["accountExternal"]},
  {code: "es", nameKey: "c.Spanish", limitedTo: ["accountExternal"]},
  {code: "sw", nameKey: "c.Swahili", limitedTo: ["accountExternal"]},
  {code: "tr", nameKey: "c.Turkish", limitedTo: ["accountExternal"]},
];

export function getLanguagesForContexts(...contexts: (LanguageContext | undefined)[]) {
  return availableLanguages.filter(lang => {
    if (!lang.limitedTo) return true;
    return lang.limitedTo.some(limitedContext => contexts.includes(limitedContext));
  });
}

export function useLanguagesForContexts(...contexts: (LanguageContext | undefined)[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => getLanguagesForContexts(...contexts), [...contexts]);
}

export function findLanguage(code: string) {
  return availableLanguages.find(lang => lang.code === code);
}

// A region that merely echoes its language (nl-NL) tells the reader nothing; one that distinguishes variants
// (en-GB vs en-US) does.
export function splitLanguageBadge(code: string): [string, string?] {
  const [language, region] = code.toLowerCase().split("-");
  return region === language ? [language] : [language, region];
}

export function convertToTolgeeLanguageCode(languageCode: string) {
  switch (languageCode) {
    case "en-US":
    case "en-GB":
      return "en";
    // Add more mappings as needed
    default:
      return languageCode;
  }
}

function convertFromTolgeeLanguageCode(tolgeeCode: string) {
  switch (tolgeeCode) {
    case "en":
      return "en-US";
    // Add more mappings as needed
    default:
      return tolgeeCode;
  }
}

const availableTolgeeLanguages = [
  ...new Set(getLanguagesForContexts("user", "internal").map(lang => convertToTolgeeLanguageCode(lang.code))),
];

const tolgee = Tolgee()
  .use(DevTools())
  .use(FormatIcu())
  // .use(LanguageDetector())  // Uncomment when we're ready to release i18n features to users
  .use(RetryingBackendFetch({prefix: tolgeeBackendPrefix}, {retries: 2, backoffMs: 200}))
  .init({
    defaultLanguage: "en",
    availableLanguages: availableTolgeeLanguages,

    // For development only
    apiUrl: import.meta.env.VITE_APP_TOLGEE_API_URL,
    apiKey: import.meta.env.VITE_APP_TOLGEE_API_KEY,
  });

export function setLanguage(languageCode: string) {
  const tolgeeCode = convertToTolgeeLanguageCode(languageCode);
  if ((tolgee.getPendingLanguage() ?? tolgee.getLanguage()) === tolgeeCode) {
    return;
  }
  tolgee.changeLanguage(tolgeeCode);
}

export function getInferredLanguage() {
  const tolgeeCode = tolgee.getPendingLanguage() ?? tolgee.getLanguage() ?? "en-US";
  return convertFromTolgeeLanguageCode(tolgeeCode);
}

export default tolgee;
